/**
 * @component TravelDairy
 * @description Главный компонент дневника путешествий. Отображает перелёты, автопоездки,
 * аналитику, карту маршрутов.
 * Поддерживает аутентификацию, настройки темы, локализацию и пагинацию.
 */

import React, { useState, useEffect } from 'react';
import { Settings, LogOut, Shield } from 'lucide-react';

import { useTranslation } from './useTranslation';
import { useAuthContext } from './services/AuthProvider';
import AuthGate from './components/auth/AuthGate';
import ThemeToggle from './components/ThemeToggle';
import { useFlightService } from './hooks/useFlightService';
import type { Flight } from './types/flight';
import {
    useCarTripService,
} from './services/CarTripService';
import { useSettingsService } from './services/SettingsService';
import { getFlights } from './services/api/flightsApi';
import { ViewGroundTripModal } from './components/ground-trip/ViewGroundTripModal';
import { useGroundTripService } from './hooks/useGroundTripService';
import type { GroundTrip } from './types/ground';
import { applyThemeToDocument } from './utils/themeStorage';
import { writeStoredSettings } from './utils/settingsStorage';
import AuthService from './services/AuthService';
import { FxRatesProvider } from './hooks/FxRatesContext';
import {
    TabNav,
    ALL_TAB_IDS,
    type TabId,
    isTripsTab,
    writeLastTransportMode,
} from './components/app/TabNav';
import { TransportTabPanel } from './components/app/TransportTabPanel';
// Lazy load heavy components
const FlightForm = React.lazy(() => import('./components/flights/FlightForm'));
const CarTripForm = React.lazy(() => import('./services/CarTripService').then(module => ({ default: module.CarTripForm })));
const ViewCarTripModal = React.lazy(() => import('./services/CarTripService').then(module => ({ default: module.ViewCarTripModal })));
const ViewFlightModal = React.lazy(() => import('./components/flights/ViewFlightModal').then(module => ({ default: module.ViewFlightModal })));
const SettingsForm = React.lazy(() => import('./services/SettingsService').then(module => ({ default: module.SettingsForm })));
const CombinedAnalytics = React.lazy(() => import('./components/analytics/CombinedAnalytics').then(module => ({ default: module.CombinedAnalytics })));
const PlanningTab = React.lazy(() => import('./components/planning/PlanningTab'));
const JourneysTab = React.lazy(() => import('./components/journeys/JourneysTab'));
const HotelsTab = React.lazy(() => import('./components/hotels/HotelsTab'));
const AdminPanel = React.lazy(() => import('./components/admin/AdminPanel'));

const LoadingSpinner = () => (
    <div className="flex justify-center items-center p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
    </div>
);

const isTabId = (v: string): v is TabId => (ALL_TAB_IDS as readonly string[]).includes(v);

const TravelDairy: React.FC = () => {
    const { isAuthenticated, loading, logout, isSuperuser } = useAuthContext();

    useEffect(() => {
        const handler = () => { logout(); };
        window.addEventListener('auth:session-expired', handler);
        return () => window.removeEventListener('auth:session-expired', handler);
    }, [logout]);

    const {
        settings, setSettings,
        selectedCarIndex, setSelectedCarIndex,
        showSettings, setShowSettings,
        syncSettingsFromServer,
    } = useSettingsService();
    const [showAdmin, setShowAdmin] = useState(false);

    useEffect(() => {
        if (!isAuthenticated || loading) return;
        void syncSettingsFromServer();
    }, [isAuthenticated, loading, syncSettingsFromServer]);

    const {
        flights,
        loading: flightsLoading,
        showAddFlight, setShowAddFlight, openNewFlightForm,
        newFlight, setNewFlight,
        addFlight,
        flightToEdit, editFlight, updateFlight, deleteFlight,
        selectedFlightId, setSelectedFlightId,
        createError: flightCreateError,
        isSavingFlight,
        flightsTotal,
        flightsPage,
        setFlightsPage,
        flightsPerPage,
        flightsFilter,
        setFlightsFilter,
        showFlightsFilter,
        setShowFlightsFilter,
        flightTags,
        loadFlightTags,
        refetchFlights,
    } = useFlightService(isAuthenticated, settings.language);

    const {
        carTrips, allTrips,
        showAddCarTrip, setShowAddCarTrip,
        newCarTrip, setNewCarTrip,
        addCarTrip, deleteCarTrip,
        viewCarTrip, editCarTrip, editCarTripAddDay, updateCarTrip,
        showViewCarTrip, setShowViewCarTrip,
        tripToView, isEditing,
        resetForm,
        currentPage,
        handlePageChange, nextPage, prevPage,
        isBuildingRoute,
        isSavingCarTrip,
    } = useCarTripService(settings, isAuthenticated);

    const readInitialTab = (): TabId => {
        try {
            const hash = (window.location.hash || '').replace(/^#/, '');
            if (isTabId(hash)) return hash;
            const stored = localStorage.getItem('mytraveldiary.activeTab') || '';
            if (isTabId(stored)) return stored;
        } catch { /* ignore */ }
        return 'flights';
    };
    const [activeTab, setActiveTab] = useState<TabId>(readInitialTab);

    const handleTabChange = (id: TabId) => {
        if (isTripsTab(id)) writeLastTransportMode(id);
        setActiveTab(id);
    };

    useEffect(() => {
        try {
            localStorage.setItem('mytraveldiary.activeTab', activeTab);
        } catch { /* ignore */ }
        if (isTripsTab(activeTab)) writeLastTransportMode(activeTab);
        const nextHash = `#${activeTab}`;
        if (window.location.hash !== nextHash) {
            window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}${nextHash}`);
        }
    }, [activeTab]);

    useEffect(() => {
        const onHash = () => {
            const hash = (window.location.hash || '').replace(/^#/, '');
            if (isTabId(hash)) setActiveTab(hash);
        };
        window.addEventListener('hashchange', onHash);
        return () => window.removeEventListener('hashchange', onHash);
    }, []);

    const busSvc = useGroundTripService('bus', isAuthenticated);
    const trainSvc = useGroundTripService('train', isAuthenticated);
    const ferrySvc = useGroundTripService('ferry', isAuthenticated);
    const [viewGroundTrip, setViewGroundTrip] = useState<GroundTrip | null>(null);
    const [flightToView, setFlightToView] = useState<Flight | null>(null);
    const [hotelsOpenSignal, setHotelsOpenSignal] = useState(0);

    const viewFlight = (flight: Flight) => setFlightToView(flight);

    const openAddCarTrip = () => {
        setShowAddCarTrip(true);
        const cars = settings?.cars || [];
        const car = cars[selectedCarIndex] ?? cars[cars.length - 1];
        if (car) {
            setNewCarTrip(prev => ({ ...prev, carMake: car.make, fuelConsumption: car.fuelConsumption ?? 0, fuelType: car.fuelType || '' }));
        }
    };

    const handleAddTransport = () => {
        if (activeTab === 'flights') openNewFlightForm();
        else if (activeTab === 'carTrips') openAddCarTrip();
        else if (activeTab === 'buses') busSvc.openNew();
        else if (activeTab === 'trains') trainSvc.openNew();
        else if (activeTab === 'ferries') ferrySvc.openNew();
        else if (activeTab === 'hotels') setHotelsOpenSignal(n => n + 1);
    };

    const [allFlightsForStats, setAllFlightsForStats] = useState<Flight[] | null>(null);
    const { t } = useTranslation(settings.language);

    useEffect(() => {
        if (activeTab !== 'analytics') {
            setAllFlightsForStats(null);
            return;
        }
        if (flightsTotal <= 0) {
            setAllFlightsForStats([]);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const data = await getFlights(settings.language, 1, Math.min(flightsTotal, 2000), null);
                if (cancelled) return;
                const items = (data?.items ?? []).map((f: any) => ({
                    ...f,
                    class: f.flightClass,
                    isInternational: f.is_international,
                    departure_airport_info: f.departure_airport_info ?? null,
                    arrival_airport_info: f.arrival_airport_info ?? null,
                }));
                setAllFlightsForStats(items);
            } catch {
                if (!cancelled) setAllFlightsForStats([]);
            }
        })();
        return () => { cancelled = true; };
    }, [activeTab, flightsTotal, settings.language]);

    useEffect(() => {
        console.log('[TravelDairy] Theme changed to:', settings.theme);
        applyThemeToDocument(settings.theme === 'dark' ? 'dark' : 'light');
    }, [settings.theme]);

    if (!isAuthenticated) {
        return <AuthGate loading={loading} />;
    }

    return (
        <FxRatesProvider currency={settings.currency ?? '₽'}>
        <div className="min-h-dvh min-h-screen bg-gradient-to-b from-stone-100 to-stone-200 dark:from-slate-900 dark:to-slate-950 text-stone-900 dark:text-slate-100 transition-colors duration-300 ease-out">
            <header className="sticky top-0 z-40 border-b border-white/20 dark:border-slate-700/50 bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl shadow-sm">
                <div className="max-w-6xl mx-auto px-3 sm:px-6 h-12 sm:h-16 flex items-center justify-between gap-2 sm:gap-4">
                    <div className="flex items-center gap-2 sm:gap-3.5 min-w-0">
                        <img
                            src="/logo-mark.webp"
                            alt="MyTravelDiary"
                            className="h-9 w-9 sm:h-12 sm:w-12 shrink-0 object-contain drop-shadow-sm"
                        />
                        <div className="min-w-0 hidden sm:block">
                            <h1 className="text-xl font-semibold truncate text-stone-900 dark:text-white">{t.appTitle}</h1>
                            <p className="text-sm text-stone-500 dark:text-slate-400 truncate">{t.appDescription}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                        <ThemeToggle
                            theme={settings.theme}
                            language={settings.language}
                            compact
                            setTheme={theme => {
                                const next = { ...settings, theme };
                                setSettings(next);
                                writeStoredSettings(next);
                                void AuthService.updatePreferences({ theme }).catch((err) =>
                                    console.warn('[Theme] save failed:', err),
                                );
                            }}
                        />
                        {isSuperuser && (
                            <button
                                type="button"
                                onClick={() => setShowAdmin(true)}
                                className="flex h-9 w-9 items-center justify-center rounded-xl border border-amber-200 dark:border-amber-800/60 bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900/50 transition-colors"
                                title={t.adminTitle}
                                aria-label={t.adminTitle}
                            >
                                <Shield className="h-4.5 w-4.5" style={{ width: '1.1rem', height: '1.1rem' }} />
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={() => setShowSettings(true)}
                            className="flex h-9 w-9 items-center justify-center rounded-xl border border-stone-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-stone-600 dark:text-slate-200 hover:bg-stone-50 dark:hover:bg-slate-700 transition-colors"
                            title={t.settings}
                            aria-label={t.settings}
                        >
                            <Settings className="h-4.5 w-4.5" style={{ width: '1.1rem', height: '1.1rem' }} />
                        </button>
                        <button
                            type="button"
                            onClick={() => void logout()}
                            className="flex h-9 w-9 sm:w-auto sm:gap-1.5 items-center justify-center rounded-xl border border-red-200 dark:border-red-900/50 bg-white dark:bg-slate-800 sm:px-3 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                            title={t.logout}
                            aria-label={t.logout}
                        >
                            <LogOut className="h-4 w-4" />
                            <span className="hidden sm:inline">{t.logout}</span>
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-3 sm:px-6 py-4 sm:py-8 pb-24 md:pb-8">
                <div className="glass-panel rounded-2xl overflow-hidden">
                    <TabNav
                        variant="desktop"
                        activeTab={activeTab}
                        onTabChange={handleTabChange}
                        language={settings.language}
                        t={t}
                    />

                    <div className="p-3 sm:p-6 min-h-[260px] sm:min-h-[280px]">
                    {isTripsTab(activeTab) && (
                        <TransportTabPanel
                            mode={activeTab}
                            onModeChange={handleTabChange}
                            t={t}
                            language={settings.language}
                            currency={settings.currency ?? '₽'}
                            flightsPanel={{
                                t,
                                language: settings.language,
                                flights,
                                flightsLoading,
                                flightsTotal,
                                flightsPage,
                                flightsPerPage,
                                setFlightsPage,
                                flightsFilter,
                                setFlightsFilter,
                                showFlightsFilter,
                                setShowFlightsFilter,
                                flightTags,
                                loadFlightTags,
                                selectedFlightId,
                                setSelectedFlightId,
                                editFlight,
                                deleteFlight,
                                viewFlight,
                                openNewFlightForm,
                            }}
                            carPanel={{
                                t,
                                allTrips,
                                carTrips,
                                currentPage,
                                handlePageChange,
                                prevPage,
                                nextPage,
                                deleteCarTrip,
                                viewCarTrip,
                                editCarTrip,
                                editCarTripAddDay,
                                isAuthenticated,
                                onAdd: openAddCarTrip,
                            }}
                            busSvc={busSvc}
                            trainSvc={trainSvc}
                            ferrySvc={ferrySvc}
                            onViewTrain={trip => setViewGroundTrip(trip)}
                            hotelsPanel={
                                <React.Suspense fallback={<LoadingSpinner />}>
                                    <HotelsTab
                                        language={settings.language}
                                        t={t}
                                        currency={settings.currency ?? 'RUB'}
                                        openCreateSignal={hotelsOpenSignal}
                                    />
                                </React.Suspense>
                            }
                        />
                    )}

                    {activeTab === 'analytics' && (
                        <React.Suspense fallback={<LoadingSpinner />}>
                            <CombinedAnalytics
                                flights={allFlightsForStats ?? flights ?? []}
                                carTrips={allTrips ?? []}
                                cars={settings.cars ?? []}
                                busTrips={busSvc.trips}
                                trainTrips={trainSvc.trips}
                                ferryTrips={ferrySvc.trips}
                                language={settings.language}
                                distanceUnit={settings.distanceUnit ?? 'km'}
                                currency={settings.currency ?? '₽'}
                                residenceCountry={settings.residenceCountry}
                            />
                        </React.Suspense>
                    )}

                    {activeTab === 'journeys' && (
                        <React.Suspense fallback={<LoadingSpinner />}>
                            <JourneysTab language={settings.language} t={t} />
                        </React.Suspense>
                    )}

                    {activeTab === 'planning' && (
                        <React.Suspense fallback={<LoadingSpinner />}>
                            <PlanningTab
                                language={settings.language}
                                currency={settings.currency ?? '₽'}
                                isAuthenticated={isAuthenticated}
                                t={t}
                            />
                        </React.Suspense>
                    )}
                    </div>
                </div>
            </main>

            <TabNav
                variant="mobile"
                activeTab={activeTab}
                onTabChange={handleTabChange}
                language={settings.language}
                t={t}
                onAddTransport={handleAddTransport}
            />

            <React.Suspense fallback={null}>
                {showAddFlight && (
                    <FlightForm
                        showAddFlight={showAddFlight}
                        setShowAddFlight={setShowAddFlight}
                        newFlight={newFlight}
                        setNewFlight={setNewFlight}
                        addFlight={addFlight}
                        language={settings.language}
                        isEditing={!!flightToEdit}
                        flightToEdit={flightToEdit}
                        updateFlight={updateFlight}
                        createError={flightCreateError}
                        isSaving={isSavingFlight}
                        onFlightsChanged={refetchFlights}
                    />
                )}
                {showAddCarTrip && (
                    <CarTripForm
                        showAddCarTrip={showAddCarTrip}
                        setShowAddCarTrip={setShowAddCarTrip}
                        newCarTrip={newCarTrip}
                        setNewCarTrip={setNewCarTrip}
                        addCarTrip={addCarTrip}
                        updateCarTrip={updateCarTrip}
                        isEditing={isEditing}
                        resetForm={resetForm}
                        settings={settings}
                        isBuildingRoute={isBuildingRoute}
                        isSavingCarTrip={isSavingCarTrip}
                    />
                )}
                {showSettings && (
                    <SettingsForm
                        showSettings={showSettings}
                        setShowSettings={setShowSettings}
                        settings={settings}
                        setSettings={setSettings}
                        selectedCarIndex={selectedCarIndex}
                        setSelectedCarIndex={setSelectedCarIndex}
                    />
                )}
                {showAdmin && isSuperuser && (
                    <AdminPanel
                        open={showAdmin}
                        onClose={() => setShowAdmin(false)}
                        t={t}
                        language={settings.language}
                    />
                )}
                {showViewCarTrip && (
                    <ViewCarTripModal
                        showViewCarTrip={showViewCarTrip}
                        setShowViewCarTrip={setShowViewCarTrip}
                        trip={tripToView}
                    />
                )}
                {flightToView && (
                    <ViewFlightModal
                        open={!!flightToView}
                        onClose={() => setFlightToView(null)}
                        flight={flightToView}
                    />
                )}
                {viewGroundTrip && (
                    <ViewGroundTripModal
                        open={!!viewGroundTrip}
                        onClose={() => setViewGroundTrip(null)}
                        trip={viewGroundTrip}
                        language={settings.language}
                        onTripPatched={patched => {
                            trainSvc.patchTrip(patched);
                            setViewGroundTrip(patched);
                        }}
                    />
                )}
            </React.Suspense>
        </div>
        </FxRatesProvider>
    );
};

export default TravelDairy;
